'use client';

import type { MessageFocus } from '@shared/all/domains/messages';
import {
  buildChatLink,
  CHAT_ROUTE,
  parseChatLink
} from '@shared/all/helpers/chatLink';
import {
  useConversationActions,
  useConversationListOpen,
  useOpenConversations
} from '@shared/utils/hooks/useConversations';
import { usePathname, useRouter } from 'next/navigation';
import {
  createContext,
  type PropsWithChildren,
  Suspense,
  useContext,
  useEffect,
  useReducer
} from 'react';

import type { VirtualChannel } from './VirtualChannelThread';

export { CHAT_ROUTE };

type ChatWorkspaceState = {
  // null = Home: the unified DM/channel list. A slug selects that space's
  // channel sidebar, Slack-workspace style.
  selectedSpaceSlug: string | null;
  // A default channel nobody has messaged in yet — no DB row exists until the
  // first send, so this thread renders client-side only.
  activeVirtual: VirtualChannel | null;
  // false = docked windows beside the page, true = the /chat full-screen
  // layout. The same components render either way.
  fullscreen: boolean;
  // Where full screen came from, so leaving it returns there instead of home.
  returnUrl: string | null;
  // A message the open chat should open at (a link or a search hit). Cleared
  // by the chat once it is on screen, so a later open of the same chat lands
  // at the bottom again.
  focus: (MessageFocus & { conversationId: string }) | null;
};

type ChatWorkspaceAction =
  | { type: 'SELECT_HOME' }
  | { type: 'SELECT_SPACE'; slug: string }
  | { type: 'OPEN_VIRTUAL'; virtual: VirtualChannel }
  | { type: 'CLOSE_VIRTUAL' }
  | { type: 'ENTER_FULLSCREEN'; returnUrl: string | null }
  | { type: 'EXIT_FULLSCREEN' }
  | { type: 'SET_FOCUS'; focus: ChatWorkspaceState['focus'] }
  | { type: 'OPEN_AT'; conversationId: string; focus?: MessageFocus };

const initialState: ChatWorkspaceState = {
  selectedSpaceSlug: null,
  activeVirtual: null,
  fullscreen: false,
  returnUrl: null,
  focus: null
};

const workspaceReducer = (
  state: ChatWorkspaceState,
  action: ChatWorkspaceAction
): ChatWorkspaceState => {
  switch (action.type) {
    case 'SELECT_HOME':
      return { ...state, selectedSpaceSlug: null, activeVirtual: null };
    case 'SELECT_SPACE':
      return state.selectedSpaceSlug === action.slug
        ? state
        : { ...state, selectedSpaceSlug: action.slug, activeVirtual: null };
    case 'OPEN_VIRTUAL':
      return { ...state, activeVirtual: action.virtual };
    case 'CLOSE_VIRTUAL':
      return { ...state, activeVirtual: null };
    case 'ENTER_FULLSCREEN':
      return { ...state, fullscreen: true, returnUrl: action.returnUrl };
    case 'EXIT_FULLSCREEN':
      return { ...state, fullscreen: false, returnUrl: null };
    case 'SET_FOCUS':
      return { ...state, focus: action.focus };
    case 'OPEN_AT':
      if (action.focus)
        return {
          ...state,
          focus: { ...action.focus, conversationId: action.conversationId }
        };
      // A plain open of the chat a jump is still pending for keeps the jump:
      // opening from a search hit moves the URL to /chat/<id>, and the chat
      // page then opens that same chat again with no message in the link.
      return state.focus?.conversationId === action.conversationId
        ? state
        : { ...state, focus: null };
  }
};

type ChatWorkspaceContextValue = {
  state: ChatWorkspaceState;
  // Whether the DM/channel sidebar shows. Chat windows and minimized pills
  // live on without it, so closing the sidebar leaves the chats in place.
  sidebarOpen: boolean;
  // Chats with a window on screen, and the ones parked as pills.
  openConversationIds: string[];
  minimizedConversationIds: string[];
  // The chat full screen shows — max one, which is what the reducer enforces.
  activeConversationId: string | null;
  openSidebar: () => void;
  closeSidebar: () => void;
  toggleSidebar: () => void;
  enterFullscreen: () => void;
  exitFullscreen: () => void;
  // Leaves full screen without touching the route, so a /chat page can show
  // its own message in place of the chat it could not open.
  dismissFullscreen: () => void;
  // `focus` opens the chat at one message (a link or a search hit) instead of
  // the bottom.
  openChat: (conversationId: string, focus?: MessageFocus) => void;
  clearFocus: () => void;
  minimizeChat: (conversationId: string) => void;
  // Parks the whole workspace as pills without closing anything, for the
  // header logo: "take me back to the site".
  minimizeAll: () => void;
  closeChat: (conversationId: string) => void;
  openVirtual: (virtual: VirtualChannel) => void;
  closeVirtual: () => void;
  selectHome: () => void;
  selectSpace: (slug: string) => void;
};

const ChatWorkspaceContext = createContext<ChatWorkspaceContextValue | null>(
  null
);

// Event handlers read the route from the window. The render path must not:
// see ChatRouteSync.
const isOnChatRoute = () => window.location.pathname.startsWith(CHAT_ROUTE);

/**
 * The workspace's only `usePathname` reader, in its own Suspense boundary.
 * Under Cache Components a `usePathname` in the provider is request data on
 * every dynamic route, which made the whole app shell — and every page inside
 * it — one dynamic hole that waited for the request before it could paint.
 * Here the hole is this component, which renders nothing.
 */
function ChatRouteSync({
  fullscreen,
  target,
  onLeaveChat
}: {
  fullscreen: boolean;
  // The /chat URL the open chat is addressed by, or null while a /chat/… link
  // is still resolving its chat.
  target: string | null;
  onLeaveChat: () => void;
}) {
  const router = useRouter();
  const pathname = usePathname();

  // Full screen IS the /chat route: the URL follows the open chat so any chat
  // can be linked to, and landing on a link opens that chat full screen.
  // With nothing open the URL is left alone — a /chat/… link is still
  // resolving its chat at that point, and rewriting it would drop the link.
  // A link to one message in the open chat is that chat's URL too, so it is
  // left as it was opened.
  useEffect(() => {
    if (!fullscreen || !target || pathname === target) return;
    const here = pathname ? parseChatLink(pathname) : null;
    if (
      here?.kind === 'conversation' &&
      buildChatLink({ conversationId: here.conversationId }) === target
    ) {
      return;
    }
    router.replace(target);
  }, [fullscreen, target, pathname]);

  // Full screen covers the page under it, so navigating anywhere else — a
  // profile link inside a message, the space directory — drops back to the
  // docked frame and reveals where it went.
  useEffect(() => {
    if (fullscreen && !pathname?.startsWith(CHAT_ROUTE)) onLeaveChat();
  }, [pathname]);

  return null;
}

/**
 * One chat workspace, two frames: windows docked beside the page, and the
 * full-screen layout /chat lands on. Both render the same components from one
 * mount in Main, so switching frames keeps the open chats, the selected space
 * and the loaded messages.
 *
 * Which chats are open, minimized or closed stays in the conversations
 * context — the same state mobile drives its pills from.
 */
export const ChatWorkspaceProvider = ({ children }: PropsWithChildren) => {
  const [state, dispatch] = useReducer(workspaceReducer, initialState);
  const {
    toggleConversationList,
    openConversation,
    minimizeConversation,
    closeConversation
  } = useConversationActions();
  // Which chats are open and whether the sidebar is: nothing else, so typing
  // or a new message does not rerender the provider.
  const openConversations = useOpenConversations();
  const sidebarOpen = useConversationListOpen();
  const router = useRouter();

  const openConversationIds = openConversations
    .filter((c) => c.conversationState === 'OPEN')
    .map((c) => c.id);
  const minimizedConversationIds = openConversations
    .filter((c) => c.conversationState === 'MINIMIZED')
    .map((c) => c.id);
  const activeConversationId = openConversationIds[0] ?? null;

  // A chat nobody has written in has no id to link to, so it is addressed by
  // the space or factiii that owns it. The first message gives it an id and
  // the URL (ChatRouteSync) switches to that.
  const virtualPath = state.activeVirtual
    ? buildChatLink({
        spaceSlug: state.activeVirtual.spaceSlug,
        factiiiId: state.activeVirtual.factiiiId
      })
    : null;
  const fullscreenTarget = activeConversationId
    ? buildChatLink({ conversationId: activeConversationId })
    : virtualPath;

  const openSidebar = () => {
    if (!sidebarOpen) toggleConversationList();
  };

  // Just the panel — no frame or route side effects. closeSidebar below is the
  // one the UI calls; this is for the paths that handle those themselves.
  const collapseSidebar = () => {
    if (sidebarOpen) toggleConversationList();
  };

  const exitFullscreen = () => {
    dispatch({ type: 'EXIT_FULLSCREEN' });
    // /chat renders nothing of its own — the workspace is the page — so going
    // back to the docked frame has to leave the route as well.
    if (isOnChatRoute()) router.push(state.returnUrl ?? '/');
  };

  // Full screen is the sidebar plus one chat (see enterFullscreen). Without the
  // sidebar it is an empty pane over the page telling you to pick a
  // conversation, with nothing left to pick from — so the frame goes too.
  const closeSidebar = () => {
    collapseSidebar();
    if (state.fullscreen) exitFullscreen();
  };

  const enterFullscreen = () => {
    // Full screen is the /chat layout: sidebar plus one chat. Without the
    // sidebar it would be a thread with no way out of it.
    openSidebar();
    dispatch({
      type: 'ENTER_FULLSCREEN',
      returnUrl: isOnChatRoute()
        ? state.returnUrl
        : window.location.pathname + window.location.search
    });
  };

  const openChat = (conversationId: string, focus?: MessageFocus) => {
    // Marks the chat read and parks whatever else was open as a pill.
    openConversation(conversationId);
    dispatch({ type: 'CLOSE_VIRTUAL' });
    dispatch({ type: 'OPEN_AT', conversationId, focus });
  };

  const openVirtual = (virtual: VirtualChannel) => {
    // A chat with no id yet gets the window, so whatever held it is parked —
    // the same swap opening a second real chat performs.
    if (activeConversationId) minimizeConversation(activeConversationId);
    dispatch({ type: 'OPEN_VIRTUAL', virtual });
  };

  const closeChat = (conversationId: string) => {
    closeConversation(conversationId);
    // Full screen held exactly this chat, so closing it hands the page back
    // rather than parking on an empty /chat that still covers the page.
    if (state.fullscreen && conversationId === activeConversationId) {
      exitFullscreen();
      collapseSidebar();
    }
  };

  // The logo goes home, and on mobile the panel covers the page it lands on.
  // Everything is parked rather than closed, so the pills still hold the
  // chats. The Link does the navigating, so this touches no route.
  const minimizeAll = () => {
    openConversationIds.forEach(minimizeConversation);
    dispatch({ type: 'CLOSE_VIRTUAL' });
    dispatch({ type: 'EXIT_FULLSCREEN' });
    collapseSidebar();
  };

  const minimizeChat = (conversationId: string) => {
    minimizeConversation(conversationId);
    // Full screen has nothing left to show once its one chat is parked, so it
    // steps aside and hands the page back.
    if (state.fullscreen) {
      exitFullscreen();
      collapseSidebar();
    }
  };

  return (
    <ChatWorkspaceContext.Provider
      value={{
        state,
        sidebarOpen,
        openConversationIds,
        minimizedConversationIds,
        activeConversationId,
        openSidebar,
        closeSidebar,
        toggleSidebar: () => (sidebarOpen ? closeSidebar() : openSidebar()),
        enterFullscreen,
        exitFullscreen,
        dismissFullscreen: () => dispatch({ type: 'EXIT_FULLSCREEN' }),
        openChat,
        clearFocus: () => dispatch({ type: 'SET_FOCUS', focus: null }),
        minimizeChat,
        minimizeAll,
        closeChat,
        openVirtual,
        closeVirtual: () => dispatch({ type: 'CLOSE_VIRTUAL' }),
        selectHome: () => dispatch({ type: 'SELECT_HOME' }),
        selectSpace: (slug: string) => dispatch({ type: 'SELECT_SPACE', slug })
      }}
    >
      <Suspense fallback={null}>
        <ChatRouteSync
          fullscreen={state.fullscreen}
          target={fullscreenTarget}
          onLeaveChat={() => dispatch({ type: 'EXIT_FULLSCREEN' })}
        />
      </Suspense>
      {children}
    </ChatWorkspaceContext.Provider>
  );
};

export const useChatWorkspace = () => {
  const context = useContext(ChatWorkspaceContext);
  if (!context) {
    throw new Error(
      'useChatWorkspace must be used inside a ChatWorkspaceProvider'
    );
  }
  return context;
};

export default ChatWorkspaceContext;
